import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { useAuth, useDictionary, useViewport, usePage } from '@/src/app/hooks';

import { Constants, getDisplayGrade, type LanguageOption } from '@/src/utils';
import { apiRequest } from '@/src/services';
import Subheader from './Subheader';
import Footer from './Footer';
import BackToTop from './BackToTop';
import Preloader from './Preloader';
import ResourcesSubheader from './ResourcesSubheader';
import './css/Menus.css';
import './css/HeaderFooter.css';

import logo from '@/src/assets/img/navigation/Paradigm-Math-logo.png';
import logoWhite from '@/src/assets/img/navigation/Paradigm-Math-logo-white.png';
import arrowDown from '@/src/assets/img/navigation/icons/down-arrow.png';
import languageEN from '@/src/assets/img/navigation/EN-icon.png';
import languageENWhite from '@/src/assets/img/navigation/EN-icon-white.png';
import languageES from '@/src/assets/img/navigation/ES-icon.png';
import languageESWhite from '@/src/assets/img/navigation/ES-icon-white.png';
import helpIcon from '@/src/assets/img/navigation/help-icon.png';
import helpIconWhite from '@/src/assets/img/navigation/help-icon-white.png';
import MathToolsButton from './MathToolsButton';
import DropdownArrow from './DropdownArrow';
import desktopDashboardCover from '@/src/assets/img/dashboard/dashboard-covers-desktop.jpg';
import mobileDashboardCover from '@/src/assets/img/dashboard/Dashboard-covers-mobile.jpg';

const Menus: React.FC = () => {
    const { user, logout } = useAuth();
    const location = useLocation();
    const { translate } = useDictionary();
    const { isMobile } = useViewport();
    const {
        isLocal,
        subheaderTitle,
        subheaderType,
        pageGrade,
        globalLoading,
        setPageGrade,
        pageLanguage,
        setPageLanguage,
    } = usePage();

    const [showLanguageToggle, setShowLanguageToggle] = useState(false);
    const [showMobileMenu, setShowMobileMenu] = useState(false);
    const [showGradesMenu, setShowGradesMenu] = useState(false);
    const [showResourcesMenu, setShowResourcesMenu] = useState(false);

    const path = location.pathname.toLowerCase();
    const hideIcons =
        !user ||
        path.includes('/account/authcallback') ||
        path.includes('/account/forgotpassword') ||
        path.includes('/account/resetpassword');
    const showSidebar =
        !!user &&
        (path.includes('/lessons') ||
            path.includes('/alllessons') ||
            path.includes('/assessments') ||
            path.includes('/dashboard'));
    const showDashboardSidebar = !!user && path.includes('/dashboard');
    // TODO: The routes above should match exactly. Weird behavior if they match like /auth/dashboard

    const mobileMenuToggleWrapper = useCallback((show: boolean) => {
        if (show) {
            document.body.classList.add('mobile-menu-open');
        } else {
            document.body.classList.remove('mobile-menu-open');
        }
        setShowMobileMenu(show);
    }, []);

    const generateGradeLink = useCallback(
        (grade: string) => {
            const url = location.pathname;
            let route = '';

            if (url.includes('assessments')) {
                route = `/resources/assessments/${grade}`;
            } else if (url.includes('allLessons')) {
                route = `/allLessons/${grade}`;
            } else {
                route = `/allLessons/${grade}`;
            }

            return (
                <Link
                    key={`gradeLink-${grade}`}
                    className={`pd-G${grade}-primary background grade-buttons`}
                    onClick={() => setPageGrade(grade)}
                    to={route}
                >
                    {translate('Grade')} {getDisplayGrade(grade)}
                </Link>
            );
        },
        [location.pathname, setPageGrade, translate]
    );

    const allowedGrades = useMemo(
        () => user?.allowedGrades || { elementary: [], middleSchool: [] },
        [user]
    );

    const generateMobileMenu = useCallback(
        (view: string) => {
            if (allowedGrades.elementary.length === 0 && allowedGrades.middleSchool.length === 0) {
                return null;
            }

            return (
                <div
                    className={`dashboard-inner ${view === 'sidebar' ? 'pd-light-grey background' : ''}`}
                >
                    {allowedGrades.elementary.length ? (
                        <div className="grade-section">
                            <h3 id="elementaryHeader" className="dashboard-labels">
                                {translate('Elementary')}
                            </h3>
                            <div className="elementary-grid grade-buttons elementary-buttons">
                                {allowedGrades.elementary.map((g) => generateGradeLink(g))}
                            </div>
                        </div>
                    ) : null}
                    {allowedGrades.middleSchool.length ? (
                        <div className="grade-section">
                            <h3 id="middleschoolHeader" className="dashboard-labels">
                                {translate('Middle School')}
                            </h3>
                            <div className="middle-school grade-buttons middle-school-buttons">
                                {allowedGrades.middleSchool.map((g) => generateGradeLink(g))}
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        },
        [allowedGrades, generateGradeLink, translate]
    );

    // TODO: Is CSS better?
    useEffect(() => {
        window.addEventListener('resize', function () {
            if (window.innerWidth > Constants.MOBILE_VIEW_MAX_WIDTH) {
                mobileMenuToggleWrapper(false);
                setShowGradesMenu(false);
                setShowResourcesMenu(false);
            }
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div id="headerMenu-container">
            <Preloader show={globalLoading} />
            <nav className="pd-header">
                <div className="nav-wrapper header-menu">
                    <div className="logo">
                        <Link to="/dashboard">
                            <img src={logo} alt="Paradigm Math Logo" />
                        </Link>
                    </div>
                    <div className="nav-content nav-menu">
                        <ul className="nav-links">
                            {user && (
                                <>
                                    <li>
                                        <Link to="/dashboard">{translate('Dashboard')}</Link>
                                    </li>
                                    <li>
                                        <Link to="/lessons">{translate('eBooks')}</Link>
                                    </li>
                                    <li>
                                        <Link
                                            to={
                                                isLocal
                                                    ? 'http://localhost:5173'
                                                    : 'https://classroom.paradigmmath.com/'
                                            }
                                        >
                                            {translate('Classroom')}
                                        </Link>
                                    </li>
                                    <li
                                        id="resources-li"
                                        className="dropdown"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Escape') {
                                                (document.activeElement as HTMLElement)?.blur();
                                            }
                                        }}
                                    >
                                        <a
                                            href="#"
                                            id="resources-toggle-desktop"
                                            className="resources-toggle-desktop"
                                            aria-haspopup="true"
                                            aria-controls="resources-dropdown-desktop"
                                        >
                                            {translate('Resources')}
                                            <img
                                                src={arrowDown}
                                                alt="Dropdown Icon"
                                                id="resources-dropdown-desktop-icon"
                                                className="dropdown-icon"
                                            />
                                        </a>
                                        <div className="resources-dropdown-desktop-content">
                                            <div
                                                id="resources-dropdown-desktop"
                                                className="resources-dropdown-desktop"
                                            >
                                                <Link
                                                    className="resource-links"
                                                    to="/resources/assessments"
                                                >
                                                    {translate('Assessments')}
                                                </Link>
                                                <Link
                                                    className="resource-links"
                                                    to="/resources/professionalLearning"
                                                >
                                                    {translate('Professional Learning')}
                                                </Link>
                                                <Link
                                                    className="resource-links"
                                                    target="_blank"
                                                    to="https://mathtools.paradigmmath.com/"
                                                >
                                                    {translate('Math Tools')}
                                                </Link>
                                                <Link
                                                    className="resource-links"
                                                    to="/resources/btbf"
                                                >
                                                    {translate('Beyond the Basic Facts')}
                                                </Link>
                                            </div>
                                        </div>
                                    </li>
                                </>
                            )}
                        </ul>
                        <div
                            className="nav-icons desktop-view"
                            style={{ display: hideIcons ? 'none' : '' }}
                        >
                            <ClickAwayListener onClickAway={() => setShowLanguageToggle(false)}>
                                <div className="language-toggle-wrapper">
                                    <a
                                        href="#"
                                        className="lang-link"
                                        id="language-toggle"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setShowLanguageToggle(!showLanguageToggle);
                                        }}
                                    >
                                        <img
                                            id="language-icon"
                                            src={pageLanguage === 'ES' ? languageES : languageEN}
                                            alt="Language"
                                        />
                                    </a>
                                    <div
                                        id="language-dropdown"
                                        className="language-dropdown"
                                        style={{
                                            display: showLanguageToggle ? 'flex' : 'none',
                                        }}
                                    >
                                        {Object.entries(Constants.LANGUAGE_OPTIONS).map(
                                            ([key, value]) => {
                                                return (
                                                    <a
                                                        key={`language-option-${key}`}
                                                        id={`language-option-${key}`}
                                                        onClick={() => {
                                                            setShowLanguageToggle(false);
                                                            setPageLanguage(key as LanguageOption);
                                                        }}
                                                    >
                                                        {value}
                                                    </a>
                                                );
                                            }
                                        )}
                                    </div>
                                </div>
                            </ClickAwayListener>
                            <Link to="/user/userGuides" className="help-link">
                                <img src={helpIcon} alt="Help" />
                            </Link>
                            <button className="background pd-primary" onClick={() => logout()}>
                                Log Out
                            </button>
                        </div>
                        <a
                            href="#"
                            id="menu-toggle"
                            className={`mobile-only ${hideIcons ? 'd-none' : ''}`}
                            onClick={(e) => {
                                e.preventDefault();
                                mobileMenuToggleWrapper(!showMobileMenu);
                            }}
                        >
                            &#9776;
                        </a>
                    </div>
                </div>
            </nav>

            {/* <!-- Popup Mobile Menu --> */}
            <div
                className={`popup-mobile-menu ${showMobileMenu ? 'active' : ''}`}
                style={{
                    transform: showMobileMenu ? 'translateX(0)' : 'translateX(-100%)',
                }}
            >
                <div className="popup-header">
                    <img className="popup-logo" src={logoWhite} alt="Paradigm Math Logo" />
                    <div className="popup-icons" style={{ display: hideIcons ? 'none' : '' }}>
                        <ClickAwayListener onClickAway={() => setShowLanguageToggle(false)}>
                            <div className="language-toggle-wrapper">
                                <a
                                    href="#"
                                    className="lang-link"
                                    id="language-toggle-mobile"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowLanguageToggle(!showLanguageToggle);
                                    }}
                                >
                                    <img
                                        id="language-icon-mobile"
                                        src={
                                            pageLanguage === 'ES'
                                                ? languageESWhite
                                                : languageENWhite
                                        }
                                        alt="Language"
                                    />
                                </a>
                                <div
                                    id="language-dropdown-mobile"
                                    className="language-dropdown"
                                    style={{
                                        display: showLanguageToggle ? 'flex' : 'none',
                                    }}
                                >
                                    {Object.entries(Constants.LANGUAGE_OPTIONS).map(
                                        ([key, value]) => {
                                            return (
                                                <a
                                                    key={`language-option-${key}-mobile`}
                                                    id={`language-option-${key}-mobile`}
                                                    onClick={() => {
                                                        setShowLanguageToggle(false);
                                                        setPageLanguage(key as LanguageOption);
                                                    }}
                                                >
                                                    {value}
                                                </a>
                                            );
                                        }
                                    )}
                                </div>
                            </div>
                        </ClickAwayListener>
                        <a href="/user/userGuides" className="help-link">
                            <img src={helpIconWhite} alt="Help" />
                        </a>
                        <a
                            href="#"
                            id="popup-menu-close"
                            onClick={(e) => {
                                e.preventDefault();
                                mobileMenuToggleWrapper(false);
                            }}
                        >
                            &times;
                        </a>
                    </div>
                </div>

                <ul className="popup-nav-links nav-menu">
                    <li>
                        <a
                            href="#"
                            id="grades-toggle"
                            className={`grades-toggle main-nav-links pd-primary text ${showGradesMenu ? 'active' : ''}`}
                            onClick={() => setShowGradesMenu(!showGradesMenu)}
                        >
                            {translate('Grades')}
                            <DropdownArrow
                                id="resources-dropdown-mobile-icon"
                                rotate={showGradesMenu ? 'up' : 'down'}
                                color="navy"
                            />
                        </a>
                        <div
                            id="grades-dropdown"
                            className={`grades-dropdown ${showGradesMenu ? 'active' : ''}`}
                        >
                            {generateMobileMenu('menu')}
                        </div>
                    </li>
                    <li>
                        <a className="main-nav-links reg-popup-nav-links" href="/dashboard">
                            {translate('Dashboard')}
                        </a>
                    </li>
                    <li>
                        <a className="main-nav-links reg-popup-nav-links" href="/lessons">
                            {translate('eBooks')}
                        </a>
                    </li>
                    <li>
                        <a
                            className="main-nav-links reg-popup-nav-links"
                            href={
                                isLocal
                                    ? 'http://localhost:5173'
                                    : 'https://classroom.paradigmmath.com/'
                            }
                        >
                            {translate('Classroom')}
                        </a>
                    </li>
                    <li>
                        <a
                            href="#"
                            id="resources-toggle-mobile"
                            className={`resources-toggle main-nav-links ${showResourcesMenu ? 'active' : ''}`}
                            onClick={() => setShowResourcesMenu(!showResourcesMenu)}
                        >
                            {translate('Resources')}
                            <DropdownArrow
                                id="resources-dropdown-mobile-icon"
                                rotate={showResourcesMenu ? 'up' : 'down'}
                                color="white"
                            />
                        </a>
                        <div
                            id="resources-dropdown-mobile"
                            className={`resources-dropdown ${showResourcesMenu ? 'active' : ''}`}
                        >
                            <a
                                className="main-nav-resources resource-links"
                                href="/resources/assessments"
                            >
                                {translate('Assessments')}
                            </a>
                            <a
                                className="main-nav-resources resource-links"
                                href="/resources/professionalLearning"
                            >
                                {translate('Professional Learning')}
                            </a>
                            <a
                                className="main-nav-resources resource-links"
                                href="https://mathtools.paradigmmath.com/"
                                target="_blank"
                            >
                                {translate('Math Tools')}
                            </a>
                            <a className="main-nav-resources resource-links" href="/resources/btbf">
                                {translate('Beyond the Basic Facts')}
                            </a>
                        </div>
                    </li>
                    <li>
                        <a
                            href="#"
                            id="logout-link-mobile"
                            className="main-nav-links reg-popup-nav-links"
                            onClick={async (e) => {
                                e.preventDefault();

                                await apiRequest('post', 'auth/logout');
                                logout();
                            }}
                        >
                            Log Out
                        </a>
                    </li>
                </ul>
            </div>

            {user && (
                <>
                    <Subheader title={subheaderTitle} type={subheaderType} grade={pageGrade} />
                    <ResourcesSubheader />
                </>
            )}

            {showSidebar ? (
                <div id="sidebar-container">
                    {/* <!-- Desktop Sidebar (Hidden in Mobile) --> */}
                    <div className="row">
                        <aside
                            className={`col-md-3 sidebar desktop-section ${isMobile ? 'd-none' : ''}`}
                        >
                            <div id="gradeSidebar">
                                {allowedGrades.elementary.length ? (
                                    <div className="grade-section">
                                        <h3 id="elementaryHeader" className="dashboard-labels">
                                            {translate('Elementary')}
                                        </h3>
                                        <div className="grade-buttons">
                                            {allowedGrades.elementary.map((g) =>
                                                generateGradeLink(g)
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                                {allowedGrades.middleSchool.length ? (
                                    <div className="grade-section">
                                        <h3 id="middleschoolHeader" className="dashboard-labels">
                                            {translate('Middle School')}
                                        </h3>
                                        <div className="grade-buttons">
                                            {allowedGrades.middleSchool.map((g) =>
                                                generateGradeLink(g)
                                            )}
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </aside>
                        {/* TODO: Only visible in mobile -- better place to put this in AllLessons? */}
                        <div
                            className={`col-12 mobile-menu text-center ${isMobile ? '' : 'd-none'}`}
                        >
                            <div id="mobileGradeMenu" className="mobile-menu-content active">
                                <div id="grades-menu" className="dashboard-box pd-grey background">
                                    <h3 className="dashboard-labels pd-primary text">Grades</h3>
                                    {generateMobileMenu('sidebar')}
                                </div>
                            </div>
                        </div>
                        <div
                            className={`${isMobile ? 'col-12' : 'col-md-9'} ${showDashboardSidebar ? 'col-xxl-6' : ''}`}
                        >
                            <Outlet />
                        </div>
                        {showDashboardSidebar && (
                            <aside
                                id="dashboard-sidebar"
                                className="col-12 col-xxl-3 order-2 order-xxl-2 d-flex d-xxl-flex"
                            >
                                <img
                                    id="dashboard-cover-mobile"
                                    className="d-xxl-none"
                                    src={mobileDashboardCover}
                                    alt="Dashboard Cover"
                                />
                                <img
                                    id="dashboard-cover-desktop"
                                    className="d-none d-xxl-block"
                                    src={desktopDashboardCover}
                                    alt="Dashboard Cover"
                                />
                            </aside>
                        )}
                    </div>
                </div>
            ) : (
                <div id="menu-outlet-wrapper">
                    <Outlet />
                </div>
            )}

            <BackToTop />

            {user && <MathToolsButton />}

            <Footer />
        </div>
    );
};

export default Menus;
